// Library imports
import { useState, useEffect } from "react";
import { Button, Image, Modal } from "react-bootstrap";
import { IoChevronBack } from "react-icons/io5";
import { MdExitToApp, MdFeedback } from "react-icons/md";
import { useNavigate, Link, useLocation } from "react-router-dom";

// Internal imports
import { useCustomerStore } from "../customer_store";
import { FrontendRoute } from "../data/route";
import * as api from "../api/backend";
import Loading from "./loading";

// Design imports
import "../style.css";

// Asset imports
import logo from "../asset/image/logo.svg";
import singpass from "../asset/image/singpass.svg";
import govtech from "../asset/image/govtech_pbgt_white.svg";

export default function Login() {
	/* Variables */

	// Hooks
	const navigate = useNavigate();
	const location = useLocation();
	const isDev = import.meta.env.DEV;

	// States
	const [isLoading, setIsLoading] = useState(false);
	const [showLoginCard, setShowLoginCard] = useState(false);
	const [isSliding, setIsSliding] = useState(false);
	const [isReturning, setIsReturning] = useState(false);
	const [showAlert, setShowAlert] = useState(false);

	// State hook to clear session and reset store
	const resetStore = useCustomerStore(state => state.resetStore);

	/* Functions */

	// Check if we should show the login card on mount (from navigation state)
	useEffect(() => {
		if (location.state?.showLoginCard) {
			setShowLoginCard(true);
			setIsSliding(false);
			setIsReturning(false);
			// Clear the state to prevent showing card on refresh
			window.history.replaceState({}, document.title);
		}
	}, [location]);

	function handleGetStartedButton() {
		// Show the login card
		setShowLoginCard(true);
		setIsSliding(false);
		setIsReturning(false);
	}

	function handleBackClick() {
		// Trigger slide-down animation
		setIsSliding(true);
		// Wait for animation to complete before hiding
		setTimeout(() => {
			setShowLoginCard(false);
			setIsSliding(false);
			setIsReturning(true);
		}, 400); // Match animation duration (0.4s)
	}

	function handleExitButton() {
		// Display alert
		setShowAlert(true);
	}

	function handleAgreeButton() {
		// Close alert and trigger exit
		setShowAlert(false);
		handleBackClick();
	}

	function handleRegisterButton() {
		// Set loading screen
		setIsLoading(true);

		// Reset all previous data
		resetStore();

		// Navigate to first step of registration
		navigate(FrontendRoute.register.detail);
	}

	function handleSingpassButton() {
		if (isDev) {
			console.log("=== SingPass Login Debug ===");
		}

		setIsLoading(true);

		api
			.SingpassLogin()
			.then(response => {
				if (isDev) console.log("SingPass response:", response);

				if (response.data && (response.data.statusCode === "302" || response.data.statusCode === 302)) {
					if (isDev) console.log("Redirecting to:", response.data.redirect_uri);
					window.location.href = response.data.redirect_uri;
				} else {
					if (isDev) console.error("Unexpected response format:", response.data);
					alert("Failed to initiate SingPass login");
				}
			})
			.catch(error => {
				if (isDev) {
					console.error("SingPass Error:", error);
					console.error("Error Response:", error.response);
				}
				alert("Failed to connect to SingPass. Please try again.");
			})
			.finally(() => {
				setIsLoading(false);
			});
	}

	// Feedback button handler
	function handleFeedbackButton() {
		window.open("https://form.gov.sg/69674f153fac4d01392592f6", "_blank", "noopener,noreferrer");
	}

	if (isLoading) return <Loading message="Redirecting to Singpass..." />;

	/* Main UI */
	return (
		<div className="register-login-background">
			{!showLoginCard ? (
				// Initial Welcome Screen
				<div className={`welcome-wrapper ${isReturning ? "welcome-fade-in" : ""}`}>
					<div className="register-login-main">
						<h1 className="register-login-title">
							PalmPass
							<span className="palmpass-logo-icon"></span>
						</h1>
						<p className="register-login-subtitle">Access Control with PalmPass</p>
						<Button
							className="register-login-button-singpass register-login-glassmorphism-button"
							onClick={handleGetStartedButton}
						>
							Get Started!
						</Button>
					</div>

					{/* Legal links and company logo at bottom */}
					<div className="register-login-footer-links">
						<Link to="/legal?tab=privacy" className="register-login-footer-link">
							Privacy Statement
						</Link>
						<span className="register-login-footer-separator">|</span>
						<Link to="/legal?tab=terms" className="register-login-footer-link">
							Terms of Use
						</Link>
					</div>

					<div className="register-login-bottom-svg">
						<Image className="register-login-bottom-logo" src={govtech} alt="GovTech" />
					</div>

					{/* Feedback button */}
					<button className="register-login-feedback-button" onClick={handleFeedbackButton}>
						<MdFeedback size={20} />
						<span>Feedback</span>
					</button>
				</div>
			) : (
				<div className={`register-login-split-layout ${isSliding ? "card-slide-down" : ""}`}>
					{/* Header Section */}
					<div className="register-login-glassmorphism-header">
						<button className="register-login-header-back-button" onClick={handleBackClick}>
							<IoChevronBack size={24} />
						</button>

						<div className="register-login-header-step-indicator">Step 1 of 2</div>

						<button className="register-login-header-exit-button" onClick={handleExitButton}>
							<MdExitToApp size={24} />
						</button>
					</div>

					{/* Content Section */}
					<div className="register-login-white-content">
						<Image className="register-login-card-logo" src={logo} alt="Palmpass logo" />

						<h2 className="register-login-content-title">Register your account</h2>

						<div className="register-login-buttons-container">
							<p className="register-login-instruction-text">
								To proceed, please log in to allow your information to be shared.
							</p>

							<Button className="register-login-button-singpass" onClick={handleSingpassButton}>
								<div className="d-flex align-items-center justify-content-center gap-2">
									<span>Retrieve Myinfo with</span>
									<Image src={singpass} alt="SingPass" />
								</div>
							</Button>

							{/* TODO: To add the manual registration in v1
                            <div className="register-login-divider">
                                <span>or</span>
                            </div>
                            
                            <Button
                                className="register-login-button-manual"
                                onClick={handleRegisterButton}
                                variant="outline-light"
                            >
                                Register Manually
                            </Button>
                            */}
						</div>
					</div>
				</div>
			)}

			{/* Exit Confirmation Modal */}
			<Modal centered onHide={() => setShowAlert(false)} show={showAlert}>
				<Modal.Header closeButton>
					<Modal.Title>Warning</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p>
						You are currently in the midst of registration, your details will not be saved if you return to homepage.
					</p>
					<p>Click Agree to go back to homepage.</p>
					<p>Click Disagree to continue with registration.</p>
				</Modal.Body>
				<Modal.Footer>
					<Button onClick={handleAgreeButton}>Agree</Button>
					<Button onClick={() => setShowAlert(false)}>Disagree</Button>
				</Modal.Footer>
			</Modal>
		</div>
	);
}
